import { Button, Text, makeStyles, tokens } from "@fluentui/react-components";
import { SendRegular, CopyRegular, CheckmarkRegular } from "@fluentui/react-icons";
import { useState } from "react";
import { formatTextToHTML } from "../utils/format";
import LoadingDots from "./LoadingDots";

const useStyles = makeStyles({
  userRow: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
  },
  userBubble: {
    maxWidth: "85%",
    backgroundColor: "var(--aco-accent)",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: "14px 14px 4px 14px",
    fontSize: "13px",
    lineHeight: "1.35",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  aiBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "100%",
  },
  aiContent: {
    fontSize: "13px",
    lineHeight: "1.4",
    color: tokens.colorNeutralForeground1,
    wordBreak: "break-word",
  },
  aiFooter: {
    display: "flex",
    gap: "2px",
    alignItems: "center",
    marginTop: "-2px",
  },
  useButton: {
    backgroundColor: "var(--aco-accent-bg)",
    color: "var(--aco-accent)",
    border: "1px solid var(--aco-accent-border)",
    borderRadius: "4px",
    height: "26px",
    paddingLeft: "8px",
    paddingRight: "8px",
    fontSize: "11px",
    fontWeight: tokens.fontWeightMedium,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: "12px",
    fontStyle: "italic",
  },
});

export default function ChatMessage({ message, isStreaming, onUseAsMail, t }) {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  if (message.role === "user") {
    return (
      <div className={styles.userRow}>
        <div className={styles.userBubble}>
          {message.content || <em style={{ opacity: 0.7 }}>{t("chat.first_message_hint")}</em>}
        </div>
      </div>
    );
  }

  const html = formatTextToHTML(message.content);
  const showFooter = !isStreaming && message.content && !message.error;

  function handleCopy() {
    const text = message.content || "";
    const ok = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(ok).catch(() => fallbackCopy(text, ok));
    } else {
      fallbackCopy(text, ok);
    }
  }

  return (
    <div className={styles.aiBlock}>
      <div className={styles.aiContent}>
        {message.error && (
          <Text className={styles.errorText}>{t("result.error")}</Text>
        )}
        {!message.error && (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        )}
        {isStreaming && !message.error && (
          <span style={{ display: "inline-block", marginLeft: "4px", verticalAlign: "middle" }}>
            <LoadingDots size="small" />
          </span>
        )}
      </div>

      {showFooter && (
        <div className={styles.aiFooter}>
          <Button
            icon={<SendRegular />}
            className={styles.useButton}
            size="small"
            onClick={onUseAsMail}
          >
            {t("btn.use_as_mail")}
          </Button>
          <Button
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            appearance="subtle"
            size="small"
            onClick={handleCopy}
            title={t("btn.copy")}
          />
        </div>
      )}
    </div>
  );
}

function fallbackCopy(text, onSuccess) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.cssText = "position:fixed;opacity:0;top:0;left:0;";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    if (document.execCommand("copy")) onSuccess();
    document.body.removeChild(ta);
  } catch {
    // ignore
  }
}
